import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router'

import { IntegrationKey } from '@ankor-io/common/types/integrations/types'
import { XeroConnectionSyncStage } from '@ankor-io/common/types/integrations/xero/types'

import { IntegrationCardsView } from './integration-cards'
import { IntegrationRunPipelineView } from './integration-run-pipeline'
import { IntegrationRunStepsView } from './integration-run-steps'
import { IntegrationRunsView } from './integration-runs'
import { IntegrationSyncJobsView } from './integration-sync-jobs'

// ─── View state ──────────────────────────────────────────────────────────────

type ViewState =
  | { screen: 'cards' }
  | { screen: 'syncJobs'; integrationKey: IntegrationKey }
  | { screen: 'runs'; integrationKey: IntegrationKey; jobId: string }
  | { screen: 'pipeline'; integrationKey: IntegrationKey; jobId: string; runId: string }
  | { screen: 'steps'; integrationKey: IntegrationKey; jobId: string; runId: string; stageKey: XeroConnectionSyncStage }

function viewToParams(view: ViewState): Record<string, string> {
  switch (view.screen) {
    case 'cards':
      return {}
    case 'syncJobs':
      return { screen: 'syncJobs', integrationKey: view.integrationKey }
    case 'runs':
      return { screen: 'runs', integrationKey: view.integrationKey, jobId: view.jobId }
    case 'pipeline':
      return { screen: 'pipeline', integrationKey: view.integrationKey, jobId: view.jobId, runId: view.runId }
    case 'steps':
      return {
        screen: 'steps',
        integrationKey: view.integrationKey,
        jobId: view.jobId,
        runId: view.runId,
        stageKey: view.stageKey,
      }
  }
}

function paramsToView(params: URLSearchParams): ViewState {
  const screen = params.get('screen')
  const integrationKey = params.get('integrationKey') as IntegrationKey | null
  const jobId = params.get('jobId')
  const runId = params.get('runId')
  const stageKey = params.get('stageKey') as XeroConnectionSyncStage | null

  if (screen === 'steps' && integrationKey && jobId && runId && stageKey) {
    return { screen: 'steps', integrationKey, jobId, runId, stageKey }
  }
  if (screen === 'pipeline' && integrationKey && jobId && runId) {
    return { screen: 'pipeline', integrationKey, jobId, runId }
  }
  if (screen === 'runs' && integrationKey && jobId) {
    return { screen: 'runs', integrationKey, jobId }
  }
  if (screen === 'syncJobs' && integrationKey) {
    return { screen: 'syncJobs', integrationKey }
  }
  return { screen: 'cards' }
}

// ─── Root component ──────────────────────────────────────────────────────────

export function OrganizationIntegration({ organizationId }: { organizationId: string }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const view = useMemo(() => paramsToView(searchParams), [searchParams])
  const setView = useCallback(
    (next: ViewState) => setSearchParams(viewToParams(next), { replace: true }),
    [setSearchParams],
  )

  if (view.screen === 'steps') {
    return (
      <IntegrationRunStepsView
        organizationId={organizationId}
        integrationKey={view.integrationKey}
        jobId={view.jobId}
        runId={view.runId}
        stageKey={view.stageKey}
        onBack={() =>
          setView({ screen: 'pipeline', integrationKey: view.integrationKey, jobId: view.jobId, runId: view.runId })
        }
      />
    )
  }

  if (view.screen === 'pipeline') {
    return (
      <IntegrationRunPipelineView
        organizationId={organizationId}
        integrationKey={view.integrationKey}
        jobId={view.jobId}
        runId={view.runId}
        onBack={() => setView({ screen: 'runs', integrationKey: view.integrationKey, jobId: view.jobId })}
        onSelectStage={(stageKey) =>
          setView({
            screen: 'steps',
            integrationKey: view.integrationKey,
            jobId: view.jobId,
            runId: view.runId,
            stageKey,
          })
        }
      />
    )
  }

  if (view.screen === 'runs') {
    return (
      <IntegrationRunsView
        organizationId={organizationId}
        integrationKey={view.integrationKey}
        jobId={view.jobId}
        onBack={() => setView({ screen: 'syncJobs', integrationKey: view.integrationKey })}
        onSelectRun={(runId) =>
          setView({ screen: 'pipeline', integrationKey: view.integrationKey, jobId: view.jobId, runId })
        }
      />
    )
  }

  if (view.screen === 'syncJobs') {
    return (
      <IntegrationSyncJobsView
        organizationId={organizationId}
        integrationKey={view.integrationKey}
        onBack={() => setView({ screen: 'cards' })}
        onSelectJob={(jobId) => setView({ screen: 'runs', integrationKey: view.integrationKey, jobId })}
        onSelectLastRun={(jobId, runId) =>
          setView({ screen: 'pipeline', integrationKey: view.integrationKey, jobId, runId })
        }
      />
    )
  }

  return (
    <IntegrationCardsView
      organizationId={organizationId}
      onSelect={(key) => setView({ screen: 'syncJobs', integrationKey: key })}
    />
  )
}

export default OrganizationIntegration
